import { BUSINESS_TIME_ZONE, businessWallClockToInstant, todayInBusinessZone } from './dates';

/*
 * Overtime. business-rules.md §5, ROADMAP.md §10.2, defect P13.
 *
 * **This is payroll arithmetic, and getting it wrong pays somebody the wrong
 * amount.** Everything here is a pure function of its arguments — no database,
 * no clock, no `Date.now()` — which is what makes it testable against numbers
 * somebody can check by hand, and what lets the server and a screen produce the
 * same figure. `occurrencesBetween` and `workingMinutesFor` live in @shared for
 * the same reason.
 *
 * ---
 *
 * **Utah follows the federal FLSA: overtime is hours over 40 in a workweek, and
 * there is no daily trigger.** Decided by the owner on 2026-08-03.
 *
 * The Airo prototype hard-codes "Overtime (8h/day)", which is California's rule
 * (P13). The difference is not academic: **four ten-hour days is zero overtime
 * under the correct rule and eight hours under the prototype's.** That case has
 * its own test, because it is the one that exposes a wrong rule — under both
 * rules a normal 8×5 week produces zero, so a five-day week proves nothing.
 *
 * ---
 *
 * **Three things it would be easy to get subtly wrong, and where each is handled:**
 *
 *   1. **Overtime is per workweek, not per pay period.** A biweekly run of 45
 *      hours then 35 is **5 hours of overtime, not zero** — the weeks do not
 *      average. `bucketByWeek` is what keeps them apart, and the caller cannot
 *      opt out because there is no function here that takes a flat total.
 *
 *   2. **The week boundary is local, and a shift crossing it splits.** Clock
 *      times are stored as UTC instants; the workweek begins at midnight in
 *      Cedar City. A shift from 21:00 Saturday to 05:00 Sunday belongs to two
 *      different weeks and is divided at the boundary — eight hours in one
 *      bucket is a week that reads as eight hours longer than it was.
 *
 *   3. **The unpaid break comes out before the threshold**, not after. Taking
 *      it off the overtime figure rather than off hours worked would move
 *      minutes from the overtime column into nothing at all.
 *
 * ---
 *
 * **The break rule is narrower than "subtract `break_minutes`", deliberately,
 * and the reason is federal law rather than taste.**
 *
 * Utah has no state break statute for adults, so the federal rules apply.
 * 29 CFR 785.19 makes a *bona fide meal period* — thirty minutes or more, fully
 * relieved of duty — unpaid. 29 CFR 785.18 makes a *rest break* of under twenty
 * minutes **compensable time that may not be deducted**, and the twenty-to-thirty
 * minute band is contested. So a scheduled break under thirty minutes is not
 * deducted here at all.
 *
 * The effect is that the calculation can never quietly shorten a paid week on
 * the strength of a coffee break somebody typed into a schedule. Where it is
 * unsure, it pays.
 *
 * **This records the arithmetic. It is not legal or tax advice** — payroll is
 * the owner's and their accountant's call, which is what business-rules.md §5
 * says too.
 */

/**
 * The rules the setting may hold.
 *
 * `weekly_over_40` is the only one selected today. The other two stay valid
 * **because the boundary is a business fact rather than a constant** — Nevada
 * has a daily overtime standard and is two hours from Cedar City, so a rule
 * change is a plausible Tuesday rather than a hypothetical. Driving it from
 * `setting` means that Tuesday is a row in a table, not a deploy.
 *
 * (The owner confirmed on 2026-08-06 that no work will ever be done in
 * California. That retires the state the prototype's rule came from; it does not
 * retire daily overtime, which is why this stays a union of three.)
 */
export const OVERTIME_RULES = ['weekly_over_40', 'daily_over_8', 'both'] as const;
export type OvertimeRule = (typeof OVERTIME_RULES)[number];

/**
 * What the screen says so nobody has to guess which rule produced the number.
 * business-rules.md §5 asks for this by name.
 */
export const OVERTIME_RULE_LABELS: Record<OvertimeRule, string> = {
  weekly_over_40: 'Over 40 hours in a week',
  daily_over_8: 'Over 8 hours in a day',
  both: 'Over 8 hours in a day, and over 40 in a week',
};

export interface OvertimeSettings {
  rule: OvertimeRule;
  /** 2400 — forty hours. */
  weeklyThresholdMinutes: number;
  /** 480 — eight hours. Unused under `weekly_over_40`. */
  dailyThresholdMinutes: number;
  /** 0 = Sunday … 6 = Saturday. The one convention in the application. */
  weekStartsOn: number;
}

export const DEFAULT_OVERTIME_SETTINGS: OvertimeSettings = {
  rule: 'weekly_over_40',
  weeklyThresholdMinutes: 2400,
  dailyThresholdMinutes: 480,
  weekStartsOn: 0,
};

/** One closed shift, as it comes out of `time_log`. Open shifts are not passed in. */
export interface ShiftInput {
  id: string;
  /** Instants, in UTC. The wall clock they mean is worked out here. */
  clockInAt: Date;
  clockOutAt: Date;
}

/** Minutes attributed to one calendar day in Cedar City. */
export interface DayMinutes {
  /** `YYYY-MM-DD`, local. */
  date: string;
  /** Clocked, before any break comes off. */
  clockedMinutes: number;
  /** What was deducted — zero unless a bona fide meal break was scheduled. */
  breakMinutes: number;
  /** `clockedMinutes - breakMinutes`, never below zero. */
  paidMinutes: number;
}

export interface WeekTotals {
  /** The local date the workweek begins — `YYYY-MM-DD`. */
  weekStart: string;
  weekEnd: string;
  days: DayMinutes[];
  clockedMinutes: number;
  breakMinutes: number;
  paidMinutes: number;
  regularMinutes: number;
  overtimeMinutes: number;
}

export interface OvertimeResult {
  weeks: WeekTotals[];
  clockedMinutes: number;
  breakMinutes: number;
  paidMinutes: number;
  regularMinutes: number;
  overtimeMinutes: number;
  rule: OvertimeRule;
  ruleLabel: string;
}

/**
 * **A break is only deducted when it is a bona fide meal period.** See the
 * header — 29 CFR 785.18 makes a short rest break paid time, and deducting one
 * is taking money off somebody for a legally compensable twenty minutes.
 */
export const MINIMUM_UNPAID_BREAK_MINUTES = 30;

export function deductibleBreakMinutes(scheduled: number): number {
  if (!Number.isFinite(scheduled) || scheduled < MINIMUM_UNPAID_BREAK_MINUTES) return 0;
  return Math.floor(scheduled);
}

// ---------------------------------------------------------------------------
// Splitting a shift across local days
// ---------------------------------------------------------------------------

const MINUTE = 60_000;

function addDays(date: string, days: number): string {
  const parsed = Date.parse(`${date}T00:00:00Z`);
  return new Date(parsed + days * 86_400_000).toISOString().slice(0, 10);
}

/** Midnight at the start of a local calendar day, as the instant it names. */
function localMidnight(date: string): number {
  return businessWallClockToInstant(date, '00:00:00').getTime();
}

/**
 * Split one shift into the local calendar days it touches.
 *
 * **The loop is the whole point.** A shift is an interval between two instants;
 * a calendar day in Cedar City is not a fixed number of milliseconds — it is 23
 * hours when the clocks go forward and 25 when they go back. So each boundary is
 * asked for by date rather than computed by adding 86,400,000, which is the
 * arithmetic that puts an hour in the wrong day twice a year.
 *
 * A shift that starts and ends on the same local day produces one entry, which
 * is every shift this business will ever record but not something to rely on:
 * a 9pm-to-5am turnover clean at a short-term rental is exactly the workflow
 * §6 of business-rules.md describes.
 */
export function splitAcrossLocalDays(shift: ShiftInput): { date: string; minutes: number }[] {
  const start = shift.clockInAt.getTime();
  const end = shift.clockOutAt.getTime();

  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];

  const out: { date: string; minutes: number }[] = [];
  let cursor = start;
  let date = todayInBusinessZone(new Date(start));

  // A shift longer than a fortnight is a forgotten clock-out, not a shift. The
  // bound stops a bad row spinning this loop rather than trusting the data.
  for (let guard = 0; guard < 15 && cursor < end; guard += 1) {
    const nextMidnight = localMidnight(addDays(date, 1));
    const segmentEnd = Math.min(end, nextMidnight);

    const minutes = Math.floor((segmentEnd - cursor) / MINUTE);
    if (minutes > 0) out.push({ date, minutes });

    cursor = segmentEnd;
    date = addDays(date, 1);
  }

  return out;
}

// ---------------------------------------------------------------------------
// Weeks
// ---------------------------------------------------------------------------

/**
 * The local date the workweek containing `date` begins on.
 *
 * Computed at UTC midnight so that no zone can shift which weekday a calendar
 * day is — the same trick `availability-service.ts`'s `dayOfWeekFor` uses, and
 * for the same reason.
 */
export function weekStartFor(date: string, weekStartsOn: number): string {
  const parsed = Date.parse(`${date}T00:00:00Z`);
  const dayOfWeek = new Date(parsed).getUTCDay();
  const back = (dayOfWeek - weekStartsOn + 7) % 7;
  return addDays(date, -back);
}

// ---------------------------------------------------------------------------
// The calculation
// ---------------------------------------------------------------------------

export interface CalculateInput {
  shifts: readonly ShiftInput[];
  /**
   * Scheduled break minutes per local date, from `availabilityService`'s
   * resolver — override wins, else the weekly pattern. A date with no entry has
   * no break, which is the same default the resolver uses.
   */
  breakMinutesByDate?: ReadonlyMap<string, number>;
  /**
   * The local calendar days this timesheet covers, inclusive. **Minutes falling
   * outside it are dropped**, and leaving this off means "keep everything".
   *
   * **This exists because of a real double-count, caught by a test.** A caller
   * has to fetch shifts that *overlap* the window rather than sit inside it —
   * a shift from 21:00 Saturday to 05:00 Sunday starts before a week beginning
   * on that Sunday, and a containment predicate would lose it entirely. But
   * having found it, the three hours worked on the Saturday belong to the
   * *previous* workweek.
   *
   * **Without this trim both weeks reported the whole shift** — measured, not
   * guessed: the week of the 2nd said eight hours and so did the week of the
   * 9th, so anybody adding two weekly timesheets together got **sixteen hours
   * for an eight-hour shift**. It doubles rather than overlaps, because the
   * overlap read finds the row from either side and the splitter then hands back
   * both of its days.
   *
   * Overlap on the way in, trim on the way out. Neither half is optional.
   */
  window?: { from: string; to: string };
  settings?: OvertimeSettings;
}

/**
 * Hours, split into regular and overtime, per workweek.
 *
 * **Every figure is whole minutes**, and every division rounds *down* — a
 * payroll figure should never round up on its own, which is the same call
 * `timeService.minutesBetween` made on clock-out.
 */
export function calculateOvertime(input: CalculateInput): OvertimeResult {
  const settings = input.settings ?? DEFAULT_OVERTIME_SETTINGS;
  const breaks = input.breakMinutesByDate ?? new Map<string, number>();

  // 1. Every shift, split into the local days it touches, then summed per day.
  //    Days outside the window are dropped — see `CalculateInput.window`; this
  //    is the half that stops a midnight-crossing shift being counted in two
  //    weekly timesheets at once.
  const window = input.window;
  const clockedByDate = new Map<string, number>();
  for (const shift of input.shifts) {
    for (const segment of splitAcrossLocalDays(shift)) {
      if (window && (segment.date < window.from || segment.date > window.to)) continue;
      clockedByDate.set(segment.date, (clockedByDate.get(segment.date) ?? 0) + segment.minutes);
    }
  }

  // 2. The unpaid break comes off the day, before any threshold is applied.
  const days: DayMinutes[] = [...clockedByDate.entries()]
    .map(([date, clockedMinutes]) => {
      const breakMinutes = Math.min(
        deductibleBreakMinutes(breaks.get(date) ?? 0),
        // A break cannot exceed the time actually clocked. Somebody who worked
        // twenty minutes and has an hour's break scheduled worked twenty
        // minutes, not minus forty.
        clockedMinutes,
      );

      return {
        date,
        clockedMinutes,
        breakMinutes,
        paidMinutes: clockedMinutes - breakMinutes,
      };
    })
    .sort((left, right) => left.date.localeCompare(right.date));

  // 3. Days into workweeks. **Not into the requested range** — the threshold is
  //    a property of the week, and a range that starts on a Wednesday still has
  //    to know the week began on the Sunday.
  const byWeek = new Map<string, DayMinutes[]>();
  for (const day of days) {
    const start = weekStartFor(day.date, settings.weekStartsOn);
    const bucket = byWeek.get(start);
    if (bucket) bucket.push(day);
    else byWeek.set(start, [day]);
  }

  const weeks: WeekTotals[] = [...byWeek.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([weekStart, weekDays]) => {
      const clockedMinutes = sum(weekDays.map((day) => day.clockedMinutes));
      const breakMinutes = sum(weekDays.map((day) => day.breakMinutes));
      const paidMinutes = sum(weekDays.map((day) => day.paidMinutes));

      const overtimeMinutes = overtimeFor(weekDays, paidMinutes, settings);

      return {
        weekStart,
        weekEnd: addDays(weekStart, 6),
        days: weekDays,
        clockedMinutes,
        breakMinutes,
        paidMinutes,
        regularMinutes: paidMinutes - overtimeMinutes,
        overtimeMinutes,
      };
    });

  return {
    weeks,
    clockedMinutes: sum(weeks.map((week) => week.clockedMinutes)),
    breakMinutes: sum(weeks.map((week) => week.breakMinutes)),
    paidMinutes: sum(weeks.map((week) => week.paidMinutes)),
    regularMinutes: sum(weeks.map((week) => week.regularMinutes)),
    overtimeMinutes: sum(weeks.map((week) => week.overtimeMinutes)),
    rule: settings.rule,
    ruleLabel: OVERTIME_RULE_LABELS[settings.rule],
  };
}

/**
 * One week's overtime, under whichever rule is configured.
 *
 * **`weekly_over_40` never looks at a single day**, and that is the whole of
 * P13. Four ten-hour days is forty paid minutes-worth of nothing over the
 * threshold — zero overtime — where a daily rule would call eight hours of it
 * overtime and pay half again for time nobody is owed it on.
 */
function overtimeFor(
  days: readonly DayMinutes[],
  paidMinutes: number,
  settings: OvertimeSettings,
): number {
  const weekly = Math.max(0, paidMinutes - settings.weeklyThresholdMinutes);
  const daily = sum(
    days.map((day) => Math.max(0, day.paidMinutes - settings.dailyThresholdMinutes)),
  );

  switch (settings.rule) {
    case 'weekly_over_40':
      return weekly;

    case 'daily_over_8':
      return daily;

    /*
     * **`both` counts each minute once**, which is the part that is easy to get
     * wrong: adding the two figures together would pay overtime twice on the
     * same hour for anybody working five ten-hour days.
     *
     * So the daily overtime is taken out first, and the weekly threshold is
     * then applied to what is left — the straight-time hours only. This is the
     * standard non-duplication treatment.
     *
     * **Nothing selects this today, and if something ever does it wants an
     * accountant's eye before it pays anybody.** A real daily-overtime
     * jurisdiction layers on rules this does not model — a double-time band
     * above twelve hours, and a seventh-consecutive-day rule. This is the
     * honest floor of that, not the whole of it, and the comment is here so the
     * next person does not assume otherwise.
     */
    case 'both': {
      const straightTime = sum(
        days.map((day) => Math.min(day.paidMinutes, settings.dailyThresholdMinutes)),
      );
      return daily + Math.max(0, straightTime - settings.weeklyThresholdMinutes);
    }
  }
}

function sum(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

/**
 * `2475` → `"41h 15m"`.
 *
 * **Minutes, never decimal hours.** `41.25` invites somebody to multiply it by a
 * rate in a spreadsheet and it invites a float; the stored figure is whole
 * minutes and this is a rendering of it, at the display edge — the same rule
 * `formatCents` follows for money.
 */
export function formatMinutes(minutes: number): string {
  const whole = Math.max(0, Math.floor(minutes));
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${String(hours)}h ${String(rest).padStart(2, '0')}m`;
}

export { BUSINESS_TIME_ZONE };
